import type { BoxPoint, CandlestickTrend, Orientation, TraceType } from '@type/grammar';
import type { MovableDirection } from './movable';

/**
 * Union type representing state at any level of the plot hierarchy.
 */
export type PlotState = FigureState | SubplotState | TraceState;

/**
 * State for the top-level figure containing metadata and current subplot state.
 */
export type FigureState
  = | {
    empty: true;
    type: 'figure';
    warning?: boolean;
  }
  | {
    empty: false;
    type: 'figure';
    title: string;
    subtitle: string;
    caption: string;
    /**
     * Figure-wide X axis label shared across all subplots (facet grids).
     * Empty string when the JSON authored no figure-level x label; callers
     * then fall back to the focused subplot's own axis.
     */
    xAxis: string;
    /**
     * Figure-wide Y axis label shared across all subplots (facet grids).
     * Empty string when the JSON authored no figure-level y label.
     */
    yAxis: string;
    size: number;
    index: number;
    subplot: SubplotState;
    traceTypes: string[];
    highlight: HighlightState; // Figure manages subplot highlighting
  };

/**
 * State for a subplot containing its index, size, and current trace state.
 */
export type SubplotState
  = | {
    empty: true;
    type: 'subplot';
    warning?: boolean;
  }
  | {
    empty: false;
    type: 'subplot';
    size: number;
    index: number;
    trace: TraceState;
    highlight: HighlightState;
  };

/**
 * Empty trace state used as a placeholder when no data is available.
 */
export interface TraceEmptyState {
  empty: true;
  type: 'trace';
  traceType: TraceType;
  audio: AudioEmptyState;
  warning?: boolean;
}

/**
 * State for a single trace/layer containing all rendering and navigation information.
 */
export type TraceState
  = | TraceEmptyState
    | {
      empty: false;
      type: 'trace';
      /** Unique identifier for the layer/trace */
      layerId: string;
      traceType: TraceType;
      plotType: string;
      title: string;
      xAxis: string;
      yAxis: string;
      z: string;
      hasMultiPoints: boolean;
      audio: AudioState;
      braille: BrailleState;
      text: TextState;
      autoplay: AutoplayState;
      highlight: HighlightState;
      /**
       * Array of audio states for all lines that intersect at the current point.
       * Used for intersection-aware audio playback in multiline plots.
       * null/undefined for normal points (single line or no intersection).
       */
      intersections?: AudioState[] | null;
      /**
       * Number of groups/series in the plot.
       * Only present for multiline plots where plotType === 'multiline'.
       */
      groupCount?: number;
      /**
       * Plot orientation, if applicable (e.g. bar, box, violin).
       */
      orientation?: Orientation;
    };

/**
 * Type narrowing for non-empty trace states with full data.
 */
export type NonEmptyTraceState = Extract<TraceState, { empty: false }>;

/**
 * Extended trace state for layer switching mode with navigation indices.
 */
export interface LayerSwitchTraceState extends NonEmptyTraceState {
  isLayerSwitch: true;
  index: number;
  size: number;
}

/**
 * Type guard to check if a trace state is in layer switch mode.
 */
export function isLayerSwitchTraceState(state: TraceState): state is LayerSwitchTraceState {
  return (
    !state.empty
    && (state as Partial<LayerSwitchTraceState>).isLayerSwitch === true
    && typeof (state as Partial<LayerSwitchTraceState>).index === 'number'
    && typeof (state as Partial<LayerSwitchTraceState>).size === 'number'
  );
}

/**
 * Minimal audio state for empty traces containing only navigation indices.
 */
export interface AudioEmptyState {
  y: number;
  x: number;
  rows: number;
  cols: number;
}

/**
 * Audio state containing frequency mapping and current value for sonification.
 */
export interface AudioState {
  freq: {
    min: number;
    max: number;
    raw: number | number[];
  };
  panning: {
    y: number;
    x: number;
    rows: number;
    cols: number;
  };
  /**
   * Group index for multiclass plots.
   * Used to determine which audio palette entry to use.
   * If undefined, defaults to 0 (single group).
   */
  group?: number;
  /**
   * Indicates whether the audio is continuous.
   * If true, the audio plays without interruption.
   * If false or undefined, the audio may have discrete segments.
   */
  isContinuous?: boolean;
  /**
   * Candlestick trend information for audio palette selection.
   * Used by AudioService to determine appropriate audio characteristics.
   * Only applicable for candlestick traces.
   */
  trend?: CandlestickTrend;
  /**
   * When true, a raw value of exactly 0 plays a percussive click instead of
   * the default low "null" tone. Used by the candlestick delta layer, where
   * zero means "exactly on the reference line" — a meaningful data point,
   * not missing data.
   */
  zeroClick?: boolean;
  /**
   * Pitch-glide direction for the tone. When set, the tone glides its pitch
   * over the note duration to convey a direction while `freq.raw` still sets
   * the base pitch: `'up'` sweeps upward (a rising "whoosh"), `'down'` sweeps
   * downward (a falling drop). Used by the candlestick delta layer so
   * above-line points rise and below-line points fall, independent of the
   * base pitch that still encodes the delta magnitude. Ignored for the zero
   * (`zeroClick`) path.
   */
  glide?: 'up' | 'down';
  /**
   * Volume multiplier for dynamic volume control.
   * Used to scale audio volume based on data characteristics (e.g., violin plot width).
   * If undefined, defaults to 1.0 (no volume scaling).
   * Range should typically be between 0.0 and 1.0, but can exceed 1.0 for emphasis.
   */
  volumeMultiplier?: number;
  /**
   * Volume scale for dynamic volume control (0-1 range).
   * Used to scale audio volume based on density values (e.g., violin plot density).
   * If undefined, defaults to 1.0 (no volume scaling).
   * Range is 0.0 to 1.0, where 0.0 = quietest and 1.0 = loudest.
   */
  volumeScale?: number;
}

/**
 * Directional guidance state for pointer/touch exploration near a curve.
 *
 * Position fields describe where the **curve point** sits relative to the
 * cursor, so they line up directly with the audio output: `curveVertical:
 * 'above'` plays a high pitch (point is up there), `curveHorizontal:
 * 'left'` pans audio to the left (point is to the left). `'center'`
 * signals exact horizontal alignment so the resolver can drop pan to zero
 * instead of choosing an arbitrary direction.
 */
export type PointerGuidanceState
  = | { onCurve: true }
    | {
      onCurve: false;
      /** Distance in screen pixels from pointer/finger to the nearest curve point center. */
      distancePx: number;
      /** Where the curve point sits vertically relative to the cursor. */
      curveVertical: 'above' | 'below';
      /** Where the curve point sits horizontally relative to the cursor. */
      curveHorizontal: 'left' | 'right' | 'center';
    };

/**
 * Union type for all braille display states across different plot types.
 */
export type BrailleState
  = | TraceEmptyState
    | BarBrailleState
    | BoxBrailleState
    | HeatmapBrailleState
    | LineBrailleState;

/**
 * Base braille state with common properties for all plot types.
 */
interface BaseBrailleState {
  id: string;
  empty: false;
  row: number;
  col: number;
  custom?: string[];
}

/**
 * Braille state for bar charts with grouped values and min/max per group.
 */
export interface BarBrailleState extends BaseBrailleState {
  values: number[][];
  min: number[];
  max: number[];
}

/**
 * Braille state for boxplots with quartile information.
 */
export interface BoxBrailleState extends BaseBrailleState {
  values: BoxPoint[];
  min: number;
  max: number;
}

/**
 * Braille state for candlestick charts with OHLC values.
 */
export interface CandlestickBrailleState extends BaseBrailleState {
  values: number[][];
  min: number;
  max: number;
}

/**
 * Braille state for line charts with multi-series values.
 */
export interface LineBrailleState extends BaseBrailleState {
  values: number[][];
  min: number[];
  max: number[];
}

/**
 * Braille state for heatmaps with 2D grid values.
 */
export interface HeatmapBrailleState extends BaseBrailleState {
  values: number[][];
  min: number;
  max: number;
}

/**
 * Axis type identifier for formatting.
 */
export type AxisType = 'x' | 'y' | 'z';

/**
 * Text description state containing labels and values for screen reader output.
 */
export interface TextState {
  main: { label: string; value: number | number[] | string };
  cross: { label: string; value: number | number[] | string };
  z?: { label: string; value: number | string };
  range?: { min: number; max: number };
  section?: string;
  /**
   * Original axis identity for main value.
   * For vertical plots: 'x', for horizontal plots: 'y'.
   * Used to apply correct formatter regardless of orientation.
   */
  mainAxis?: AxisType;
  /**
   * Original axis identity for cross value.
   * For vertical plots: 'y', for horizontal plots: 'x'.
   * Used to apply correct formatter regardless of orientation.
   */
  crossAxis?: AxisType;
  /**
   * Range for the cross axis, used in grid navigation to show both axis ranges.
   */
  crossRange?: { min: number; max: number };
  /**
   * Points in the current grid cell, listed as coordinate pairs.
   */
  gridPoints?: { x: number; y: number }[];
  /**
   * Current grid cell position (1-indexed for display).
   */
  gridPosition?: { row: number; col: number };
}

/**
 * Autoplay state mapping directions to point counts for continuous playback.
 */
export type AutoplayState = {
  [key in MovableDirection]: number;
};

/**
 * A single statistic entry for chart description (e.g., "Min: 5", "Groups: 3").
 */
export interface DescriptionStat {
  label: string;
  value: string | number;
}

/**
 * Summary of a single subplot in a multi-panel figure, used to give users
 * an at-a-glance list of what's available before they navigate in.
 */
export interface SubplotSummary {
  /** 1-based visual index (top-left = 1, reading order). */
  index: number;
  /** Layer title for the subplot, or empty string if not available. */
  title: string;
  /** Trace type label(s) for the subplot's layers. */
  traceTypes: string[];
  /** True if this is the subplot the user is currently focused on. */
  isActive: boolean;
}

/**
 * Description state containing objective chart metadata for the description modal.
 * Fetched on-demand when the user opens the description dialog.
 */
export interface DescriptionState {
  /** Chart/trace type label (e.g., "Bar Chart", "Line Plot") */
  chartType: string;
  /** Chart title */
  title: string;
  /** Axes information */
  axes: {
    x?: string;
    y?: string;
    z?: string;
  };
  /** Chart-specific summary statistics */
  stats: DescriptionStat[];
  /** Data table for raw data display */
  dataTable: {
    headers: string[];
    rows: (string | number)[][];
  };
  /**
   * List of all subplots in the figure, populated only when the figure has
   * more than one subplot. Lets users see what other subplots are available
   * without having to navigate through them.
   */
  subplots?: SubplotSummary[];
}

/**
 * Highlight state for visual emphasis of current plot elements.
 */
export type HighlightState
  = | {
    empty: true;
    type: 'trace';
    traceType?: TraceType;
    audio: AudioEmptyState;
  }
  | {
    empty: false;
    elements: SVGElement | SVGElement[];
  };
